import React from "react";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import IconPickerModal, {
  useSpecsEditorIconPickerApi,
  type SpecsEditorIconPickerApi,
} from "./IconPickerModal";
import SpecsEditorPromptDialog, {
  useSpecsEditorPromptDialogApi,
  type SpecsEditorPromptDialogApi,
} from "./SpecsEditorPromptDialog";
import SpecsDuplicateProductDialog, {
  useSpecsDuplicateProductDialogApi,
  type SpecsDuplicateProductDialogApi,
} from "./SpecsDuplicateProductDialog";
import SpecsValidationDialog, {
  useSpecsValidationDialogApi,
  type SpecsValidationDialogApi,
} from "./SpecsValidationDialog";
import { uploadSpecsImageFile } from "./specsImageUpload";

export type SpecsEditorModalsApi = SpecsEditorIconPickerApi &
  SpecsEditorPromptDialogApi &
  SpecsDuplicateProductDialogApi &
  SpecsValidationDialogApi;

export function useSpecsEditorModals(): SpecsEditorModalsApi {
  const iconPickerModal = useSpecsEditorIconPickerApi();
  const promptDialog = useSpecsEditorPromptDialogApi();
  const duplicateProductDialog = useSpecsDuplicateProductDialogApi();
  const validationDialog = useSpecsValidationDialogApi();

  return React.useMemo(
    () => ({
      ...iconPickerModal,
      ...promptDialog,
      ...duplicateProductDialog,
      ...validationDialog,
    }),
    [duplicateProductDialog, iconPickerModal, promptDialog, validationDialog]
  );
}

function getIconPickerFieldPath(
  target: NonNullable<SpecsEditorModalsApi["iconPickerTarget"]>
) {
  if (target.kind === "element") {
    return `elements.${target.elementName}.menuIconUrl`;
  }

  return `elements.${target.elementName}.styles.${target.styleName}.menuIconUrl`;
}

function applyIconPickerUrl(
  editor: ReturnType<typeof useSpecsEditorContextValue>["editor"],
  target: NonNullable<SpecsEditorModalsApi["iconPickerTarget"]>,
  url: string
) {
  if (target.kind === "element") {
    editor.updateElement(
      target.elementName,
      { menuIconUrl: url },
      target.partName
    );
    return;
  }

  editor.updateStyleIcon(
    target.elementName,
    target.styleName,
    url,
    target.partName
  );
}

export default function SpecsEditorModals() {
  const { editor, importSpecs, modals } = useSpecsEditorContextValue();

  return (
    <>
      <IconPickerModal
        open={modals.iconPickerTarget !== null}
        specs={importSpecs}
        onSelect={(url) => {
          if (!modals.iconPickerTarget) return;
          applyIconPickerUrl(editor, modals.iconPickerTarget, url);
          modals.closeIconPicker();
        }}
        onUpload={(file) => {
          if (!modals.iconPickerTarget) {
            return Promise.resolve();
          }
          const target = modals.iconPickerTarget;
          const fieldPath = getIconPickerFieldPath(target);

          return uploadSpecsImageFile(file, {
            specType: "product",
            specName: editor.identity.productName || undefined,
            fieldPath,
          }).then((url) => {
            applyIconPickerUrl(editor, target, url);
            modals.closeIconPicker();
          });
        }}
        onClose={modals.closeIconPicker}
      />

      <SpecsEditorPromptDialog
        promptDialog={modals.promptDialog}
        onClose={modals.closePromptDialog}
      />

      <SpecsDuplicateProductDialog />

      <SpecsValidationDialog
        open={modals.validationOpen}
        validationResult={modals.validationResult}
        onClose={modals.closeValidationDialog}
      />
    </>
  );
}
